import { useEffect, useState } from "react";
import { JSX } from "react";
import { useNavigate, useLocation } from "react-router";
import { motion } from "framer-motion";
import useAuth from "../../../shared/hooks/use-auth";
import { hasActiveSubscription } from "../../../shared/guards/subscription.guard";
import {
  subscriptionControllerGetActiveSubscription,
  Subscription,
} from "@/shared/client";
import { HttpStatusCode } from "axios";
import {
  LoaderIcon,
  CreditCard,
  Lock,
  Sparkles,
  ArrowRight,
} from "lucide-react";
import { useTranslations } from "use-intl";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";

interface SubscriptionProtectedRouteProps {
  children: JSX.Element;
}

const SubscriptionProtectedRoute = ({
  children,
}: SubscriptionProtectedRouteProps) => {
  const {
    selectedOrganization,
    isLoading: authLoading,
    hasSystemRole,
  } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const t = useTranslations("subscription");
  const [subscription, setSubscription] = useState<Subscription | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // Exclude subscription management pages from this check
  const isSubscriptionPage =
    location.pathname.startsWith("/admin/subscription") ||
    location.pathname.startsWith("/admin/paddle");

  // Check if user has system role (admin or support) - they bypass subscription check
  const userHasSystemRole = hasSystemRole;

  useEffect(() => {
    // Skip check for subscription pages
    if (isSubscriptionPage) {
      setIsLoading(false);
      return;
    }

    // Skip subscription check for system role users
    if (userHasSystemRole) {
      setIsLoading(false);
      return;
    }

    // Wait for auth to load
    if (authLoading || !selectedOrganization) {
      return;
    }

    const checkSubscription = async () => {
      try {
        setIsLoading(true);
        const response = await subscriptionControllerGetActiveSubscription({
          query: {
            organizationId: selectedOrganization.id,
          },
        });

        if (
          response.data &&
          response.status === HttpStatusCode.Ok &&
          response.data !== null
        ) {
          setSubscription(response.data);
        } else {
          setSubscription(null);
        }
      } catch {
        // 404 is expected when no subscription exists
        setSubscription(null);
      } finally {
        setIsLoading(false);
      }
    };

    checkSubscription();
  }, [
    selectedOrganization,
    authLoading,
    isSubscriptionPage,
    userHasSystemRole,
  ]);

  // Show loading state
  if (authLoading || isLoading) {
    return (
      <div className="min-h-screen bg-background">
        <div className="flex items-center justify-center h-screen">
          <LoaderIcon
            height={40}
            width={40}
            className="animate-spin text-muted-foreground"
          />
        </div>
      </div>
    );
  }

  // Allow access to subscription pages
  if (isSubscriptionPage) {
    return children;
  }

  // Allow access for system role users (admin or support) - they bypass subscription requirement
  if (userHasSystemRole) {
    return children;
  }

  // Block access if no active subscription
  if (!hasActiveSubscription(subscription)) {
    const getStatusText = (status: Subscription["status"] | null) => {
      if (!status) return t("inactive") || "Inactive";
      switch (status) {
        case "past_due":
          return t("past_due") || "Past Due";
        case "canceled":
          return t("canceled") || "Canceled";
        case "paused":
          return t("paused") || "Paused";
        default:
          return t("inactive") || "Inactive";
      }
    };

    const getStatusVariant = (status: Subscription["status"] | null) => {
      if (!status) return "secondary";
      switch (status) {
        case "past_due":
          return "destructive";
        case "canceled":
        case "paused":
          return "secondary";
        default:
          return "secondary";
      }
    };

    return (
      <div className="min-h-screen bg-gradient-to-br from-background via-background to-muted/20">
        <div className="flex items-center justify-center min-h-screen p-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, ease: "easeOut" }}
            className="w-full max-w-lg"
          >
            <Card className="border-2 shadow-xl">
              <CardHeader className="text-center space-y-6 pb-8">
                <motion.div
                  initial={{ scale: 0.8, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  transition={{ delay: 0.2, duration: 0.5 }}
                  className="flex justify-center"
                >
                  <div className="relative">
                    <div className="absolute inset-0 bg-primary/20 rounded-full blur-2xl animate-pulse" />
                    <div className="relative flex items-center justify-center w-24 h-24 rounded-full bg-primary/10 border-2 border-primary/20">
                      <Lock className="h-12 w-12 text-primary" />
                    </div>
                  </div>
                </motion.div>
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.3 }}
                  className="space-y-2"
                >
                  <CardTitle className="text-3xl font-bold">
                    {t("subscription_required") || "Subscription Required"}
                  </CardTitle>
                  <CardDescription className="text-base">
                    {t("subscription_required_description") ||
                      "Your organization needs an active subscription to access this service."}
                  </CardDescription>
                </motion.div>
              </CardHeader>
              <CardContent className="space-y-6">
                {subscription && (
                  <motion.div
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.4 }}
                    className="flex items-center justify-between p-4 bg-muted/50 rounded-lg border"
                  >
                    <div className="flex items-center gap-3">
                      <Sparkles className="h-5 w-5 text-muted-foreground" />
                      <div>
                        <p className="text-sm text-muted-foreground">
                          {t("current_status") || "Current status"}
                        </p>
                        <Badge
                          variant={getStatusVariant(subscription.status)}
                          className="mt-1"
                        >
                          {getStatusText(subscription.status)}
                        </Badge>
                      </div>
                    </div>
                  </motion.div>
                )}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.5 }}
                  className="space-y-3"
                >
                  <Button
                    onClick={() => navigate("/admin/subscription/checkout")}
                    className="w-full h-12 text-base font-semibold"
                    size="lg"
                  >
                    <CreditCard className="mr-2 h-5 w-5" />
                    {t("view_subscription_plans") || "View Subscription Plans"}
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => navigate("/admin/subscription")}
                    className="w-full h-11"
                  >
                    {t("manage_subscription") || "Manage Subscription"}
                  </Button>
                </motion.div>
              </CardContent>
            </Card>
          </motion.div>
        </div>
      </div>
    );
  }

  // Allow access if subscription is active
  return children;
};

export default SubscriptionProtectedRoute;
